import { Badge, Button, Field, Input } from '@cloudflare/kumo';
import { SidePanel } from './ui/SidePanel';
import { useCallback, useEffect, useState } from 'react';
import { PasswordField } from './ui/PasswordField';
import { ICONS, Glyph } from './ui/icons';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';

interface PlatformEmail {
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpFrom: string;
  hasPassword: boolean;
  configured: boolean;
  fromEnvironment: boolean;
}

/**
 * SMTP da plataforma (Plataforma › Integrações). Envia os convites para
 * organizações; sem ele, quem convida copia o link manualmente.
 */
export function PlatformEmailCard() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<PlatformEmail | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<{ ok: boolean; text: string } | null>(null);
  const [form, setForm] = useState({ smtpHost: '', smtpPort: '587', smtpUser: '', smtpPassword: '', smtpFrom: '' });

  const load = useCallback(async () => {
    const res = await fetchApi('/api/settings/platform-email');
    if (res.ok) setData(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const openModal = () => {
    setForm({
      smtpHost: data?.smtpHost || '',
      smtpPort: String(data?.smtpPort || 587),
      smtpUser: data?.smtpUser || '',
      smtpPassword: '',
      smtpFrom: data?.smtpFrom || '',
    });
    setOpen(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetchApi('/api/settings/platform-email', { method: 'PUT', body: JSON.stringify(form) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Erro ao salvar o SMTP');
      setData(body);
      setOpen(false);
      toast.success('SMTP da plataforma salvo');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar o SMTP');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      const res = await fetchApi('/api/settings/platform-email/test', { method: 'POST', body: '{}' });
      const body = await res.json().catch(() => ({}));
      setTest(body.ok ? { ok: true, text: `E-mail de teste enviado para ${body.to}` } : { ok: false, text: body.error || 'Falha no envio' });
    } finally {
      setTesting(false);
    }
  };

  const set = (key: keyof typeof form) => (value: string) => setForm((f) => ({ ...f, [key]: value }));
  const configured = !!data?.configured || !!data?.fromEnvironment;

  return (
    <>
      <div className="settings-card">
        <div>
          <div className="flex items-center justify-between gap-2">
            <span style={{ height: 28, display: 'flex', alignItems: 'center', color: 'var(--brand-text)' }}><ICONS.Email size={32} /></span>
            {configured ? <Badge variant="green">Configurado</Badge> : <Badge variant="neutral">Não configurado</Badge>}
          </div>
          <div className="font-semibold" style={{ marginTop: 12 }}>E-mail (SMTP)</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Envia os convites para organizações</div>
          <div className="integration-host" title={data?.smtpHost || undefined}>
            <ICONS.Link size={16} />
            <span>{data?.smtpHost ? `${data.smtpHost}:${data.smtpPort}` : data?.fromEnvironment ? 'definido no ambiente' : 'sem servidor'}</span>
          </div>
          {test && (
            <div className={`integration-test ${test.ok ? 'integration-test--ok' : 'integration-test--fail'}`} role="status">
              <Glyph icon={test.ok ? ICONS.CheckmarkWithCircle : ICONS.Warning} size={16}/>
              <span>{test.text}</span>
            </div>
          )}
        </div>
        <div className="settings-card__actions">
          <Button variant="secondary" icon={configured ? ICONS.Edit : ICONS.Plus} onClick={openModal}>
            {configured ? 'Editar' : 'Configurar'}
          </Button>
          <Button variant="secondary" icon={ICONS.Email} disabled={!configured} loading={testing} onClick={runTest}>
            Enviar teste
          </Button>
        </div>
      </div>

      <SidePanel open={open} onClose={() => setOpen(false)} title="E-mail da plataforma (SMTP)" description="Usado para os convites de membros. Cada organização pode ter o próprio SMTP para as notificações de campanha." formId="email-form" submitting={saving}>
        <form id="email-form" noValidate onSubmit={save} className="flex flex-col gap-4">
          <div className="flex gap-3"><div className="min-w-0 flex-1">
            <Field label="Servidor">
              <Input onChange={(e) => set('smtpHost')(e.target.value)} placeholder="smtp.exemplo.com" value={form.smtpHost} required={true} />
            </Field>
          </div>
          <div className="w-28 shrink-0 [&_input]:!w-full [&>*]:!w-full"><Field label="Porta">
            <Input className="w-full min-w-0" onChange={(e) => set('smtpPort')(e.target.value)} type="number" placeholder="587" value={form.smtpPort} />
          </Field></div></div>
          <div>
            <Field label="Usuário">
              <Input onChange={(e) => set('smtpUser')(e.target.value)} autoComplete="off" value={form.smtpUser} required={true} />
            </Field>
          </div>
          <div>
            <PasswordField
              label="Senha"
              autoComplete="new-password"
              value={form.smtpPassword}
              onChange={set('smtpPassword')}
              placeholder={data?.hasPassword ? 'Deixe em branco para manter a atual' : ''}
            />
          </div>
          <div>
            <Field label="Remetente (opcional)" description="Sem remetente, os e-mails saem com o usuário SMTP">
              <Input onChange={(e) => set('smtpFrom')(e.target.value)} placeholder="Ingrave <nao-responda@ingrave.com.br>" value={form.smtpFrom} />
            </Field>
          </div>
        </form>
      </SidePanel>
    </>
  );
}
